/**
 * DTA Collaboration API service.
 */

import { sdk, sdkCall } from "./sdk"

import type {
  CommentsListResponse,
  ProposalsListResponse,
  Comment,
  Proposal,
  CreateProposalRequest,
} from "@/generated/sdk"

export type { Comment, Proposal, CreateProposalRequest }

export interface CreateCommentRequest {
  section_reference: string
  comment_text: string
  vendor_company: string
  version: string
}

export const dtaCollaborationApi = {
  getComments: async (
    dtaId: string,
    section?: string,
    version?: string,
  ): Promise<CommentsListResponse> => {
    return sdkCall(sdk.listDtaComments({ dtaId, section, version }))
  },

  createComment: async (dtaId: string, request: CreateCommentRequest): Promise<Comment> => {
    try {
      return await sdkCall(
        sdk.createComment({
          dtaId,
          createCommentRequest: request,
        }),
      )
    } catch (err) {
      throw new Error(
        `Failed to create comment: ${err instanceof Error ? err.message : "unknown error"}`,
      )
    }
  },

  updateComment: async (
    dtaId: string,
    commentId: string,
    status: "PENDING" | "RESOLVED" | "REJECTED",
    resolutionComment?: string,
  ): Promise<Comment> => {
    return sdkCall(
      sdk.updateComment({
        dtaId,
        commentId,
        updateCommentRequest: { status, resolution_comment: resolutionComment },
      }),
    )
  },

  getProposals: async (
    dtaId: string,
    section?: string,
    status?: string,
    version?: string,
  ): Promise<ProposalsListResponse> => {
    return sdkCall(sdk.listDtaProposals({ dtaId, section, status, version }))
  },

  createProposal: async (dtaId: string, request: CreateProposalRequest): Promise<Proposal> => {
    try {
      return await sdkCall(
        sdk.createProposal({
          dtaId,
          createProposalRequest: request,
        }),
      )
    } catch (err) {
      throw new Error(
        `Failed to create proposal: ${err instanceof Error ? err.message : "unknown error"}`,
      )
    }
  },

  updateProposal: async (
    dtaId: string,
    proposalId: string,
    status: "ACCEPTED" | "REJECTED" | "MODIFIED",
    reviewComment?: string,
    appliedVersion?: number,
  ): Promise<Proposal> => {
    return sdkCall(
      sdk.updateProposal({
        dtaId,
        proposalId,
        updateProposalRequest: {
          status,
          review_comment: reviewComment,
          applied_version: appliedVersion,
        },
      }),
    )
  },
}
